import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  formatMetricValue,
  formatThresholdValue,
  getMetricDefinition,
} from "@situate/contracts/metrics";
import type { AlertActionResponse, AlertDetailResponse } from "@situate/contracts/runtime";
import {
  alertActionResponseSchema,
  alertDetailResponseSchema,
} from "@situate/contracts/runtime-zod";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import { ApiError } from "@/shared/api/api-error";
import { ConsoleSurface, RightRailContainer, SeverityAccent, StatusBadge } from "@/shared/ui";
import { formatNotificationCreatedAt } from "@/features/deployment-dashboard/notification-center/notification-stream-item-format";

interface AlertDetailsPanelProps {
  alertId: string;
  onBack?: () => void;
  onClose?: () => void;
  floating?: boolean;
}

function getScopeLabel(
  scope: AlertDetailResponse["scope"],
  scopeDisplay: AlertDetailResponse["scope_display"]
): string {
  const displayName =
    scope.type === "area"
      ? scopeDisplay.area_name
      : scope.type === "queue"
        ? scopeDisplay.queue_display_name
        : scope.type === "aoi"
          ? scopeDisplay.aoi_name
          : scopeDisplay.line_display_name;
  const scopeType = `${scope.type[0].toUpperCase()}${scope.type.slice(1)}`;
  return displayName === null ? scopeType : `${scopeType}: ${displayName}`;
}

function getOperatorLabel(operator: string): string {
  switch (operator) {
    case ">=":
      return "At least";
    case ">":
      return "More than";
    case "<=":
      return "At most";
    case "<":
      return "Less than";
    case "=":
      return "Exactly";
    default:
      return operator;
  }
}

function getEvidencePresentation(
  condition: AlertDetailResponse["observation_history"][number]["evidence"]["matched_conditions"][number]
) {
  const definition = getMetricDefinition(condition.metric.source, condition.metric.name);
  const observedValue = formatMetricValue(
    condition.metric.source,
    condition.metric.name,
    condition.metric.observed_value
  );
  let thresholdValue: string;
  try {
    thresholdValue = formatThresholdValue(
      condition.metric.source,
      condition.metric.name,
      condition.threshold_snapshot.configured_value,
      condition.threshold_snapshot.value_mode,
      condition.capacity_snapshot_value
    );
  } catch {
    thresholdValue = "Threshold unavailable";
  }
  return {
    metricName: definition.display_name,
    observedValue,
    thresholdValue,
    operatorLabel: getOperatorLabel(condition.threshold_snapshot.operator),
  };
}

type AlertSubject = AlertDetailResponse["resolved_by"];
type AlertObservation = AlertDetailResponse["observation_history"][number];
type Action = "resolve";

type TimelineEvent =
  | { type: "observation"; observation: AlertObservation; previous: AlertObservation | null }
  | { type: "resolve"; timestamp: string; subject: AlertSubject; reason: string | null };

function getSubjectPresentation(subject: AlertSubject): { label: string; isSystem: boolean } {
  if (subject === null) return { label: "Unknown subject", isSystem: false };
  const subjectType = subject.subject_type.toLowerCase();
  if (subjectType === "system" || subjectType === "service") {
    return { label: "System", isSystem: true };
  }
  if (subject.display_name !== null) return { label: subject.display_name, isSystem: false };
  return {
    label:
      subject.subject_id === null
        ? subject.subject_type
        : `${subject.subject_type}: ${subject.subject_id}`,
    isSystem: false,
  };
}

function ReasonText({ reason }: { reason: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const hasLongReason = (reason?.length ?? 0) > 160;

  return (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={
          hasLongReason && !expanded
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : undefined
        }
      >
        Reason: {reason ?? "N/A"}
      </Typography>
      {hasLongReason && (
        <Button
          variant="text"
          size="small"
          onClick={() => setExpanded((value) => !value)}
          sx={{ minWidth: 0, mt: 0.25, p: 0 }}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
}

function LifecycleActionEvent({ event }: { event: Extract<TimelineEvent, { type: "resolve" }> }) {
  const subjectPresentation = getSubjectPresentation(event.subject);
  return (
    <ConsoleSurface sx={{ p: 1.5, display: "grid", gap: 0.75 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Resolved
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
        {formatNotificationCreatedAt(event.timestamp)}
      </Typography>
      <Typography variant="body2">
        Actor: <strong>{subjectPresentation.label}</strong>
      </Typography>
      <ReasonText reason={event.reason} />
    </ConsoleSurface>
  );
}

type MatchedCondition = AlertObservation["evidence"]["matched_conditions"][number];

function ValueChangeArrow({ direction, label }: { direction: "up" | "down"; label: string }) {
  return direction === "up" ? (
    <ArrowUpwardIcon
      aria-label={`${label} increased`}
      sx={{ color: "error.main", fontSize: "1rem", verticalAlign: "middle", ml: 0.5 }}
    />
  ) : (
    <ArrowDownwardIcon
      aria-label={`${label} decreased`}
      sx={{ color: "success.main", fontSize: "1rem", verticalAlign: "middle", ml: 0.5 }}
    />
  );
}

function getValueDirection(current: number, previous: number | undefined): "up" | "down" | null {
  if (previous === undefined || current === previous) return null;
  return current > previous ? "up" : "down";
}

function EvidenceCondition({
  condition,
  previous,
}: {
  condition: MatchedCondition;
  previous?: MatchedCondition;
}) {
  const evidence = getEvidencePresentation(condition);
  const observedDirection = getValueDirection(
    condition.metric.observed_value,
    previous?.metric.observed_value
  );
  return (
    <Box
      sx={(theme) => ({
        p: 1.25,
        borderRadius: 1,
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        display: "grid",
        gap: 1,
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {evidence.metricName}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Observed
            {observedDirection !== null && (
              <ValueChangeArrow direction={observedDirection} label="Observed value" />
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {evidence.observedValue}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Threshold
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {evidence.thresholdValue}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Triggered when the value is {evidence.operatorLabel.toLowerCase()} the threshold.
      </Typography>
    </Box>
  );
}

function ObservationEvent({
  observation,
  previous,
}: {
  observation: AlertObservation;
  previous: AlertObservation | null;
}) {
  const previousConditions = new Map(
    previous?.evidence.matched_conditions.map((condition) => [condition.condition_id, condition]) ??
      []
  );
  const severityChanged = previous !== null && previous.severity !== observation.severity;
  return (
    <ConsoleSurface sx={{ p: 1.5, display: "grid", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {previous === null
            ? "Alert raised"
            : severityChanged
              ? "Severity changed"
              : "Observation updated"}
        </Typography>
        {previous === null && (
          <SeverityAccent level={observation.severity} label={observation.severity} />
        )}
      </Box>
      {severityChanged && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
          aria-label="severity change"
        >
          <SeverityAccent level={previous.severity} label={previous.severity} />
          <Typography aria-hidden="true">→</Typography>
          <SeverityAccent level={observation.severity} label={observation.severity} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        {formatNotificationCreatedAt(observation.observed_at)}
      </Typography>
      {observation.evidence.matched_conditions.map((condition) => (
        <EvidenceCondition
          key={condition.condition_id}
          condition={condition}
          previous={previousConditions.get(condition.condition_id)}
        />
      ))}
    </ConsoleSurface>
  );
}

export function AlertDetailsPanel({
  alertId,
  onBack,
  onClose,
  floating = false,
}: AlertDetailsPanelProps) {
  const runtime = useRuntimeHttpClient();
  const queryClient = useQueryClient();
  const [mutationErrorMessage, setMutationErrorMessage] = useState<string | null>(null);
  const [dialogAction, setDialogAction] = useState<Action | null>(null);
  const [reason, setReason] = useState("");
  const hasAlertDetailGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "alert_detail"
  );
  function closeActionDialog(): void {
    setDialogAction(null);
    setReason("");
    setMutationErrorMessage(null);
  }

  function openActionDialog(action: Action): void {
    mutation.reset();
    setMutationErrorMessage(null);
    setReason("");
    setDialogAction(action);
  }

  const detailQuery = useQuery({
    queryKey: ["alert-detail", runtime.deploymentId, alertId],
    queryFn: async (): Promise<AlertDetailResponse> => {
      if (runtime.client === null) throw new Error("Runtime client is unavailable");
      const response = await runtime.client.get<unknown>(
        `/v1/alerts/${encodeURIComponent(alertId)}`
      );
      return alertDetailResponseSchema.parse(response);
    },
    enabled: runtime.client !== null && runtime.deploymentId !== null && hasAlertDetailGrant,
    refetchInterval: 30_000,
  });

  const mutation = useMutation({
    mutationFn: async ({
      action,
      reason,
    }: {
      action: Action;
      reason: string;
    }): Promise<AlertActionResponse> => {
      if (runtime.client === null) throw new Error("Runtime client is unavailable");
      const body = reason === "" ? {} : { resolve_reason: reason };
      const response = await runtime.client.post<unknown>(
        `/v1/alerts/${encodeURIComponent(alertId)}/${action}`,
        body
      );
      return alertActionResponseSchema.parse(response);
    },
    onMutate: () => {
      setMutationErrorMessage(null);
    },
    onSuccess: () => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["alert-detail", runtime.deploymentId, alertId],
        }),
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-alert-count"] }),
      ]);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.statusCode === 409) {
        setMutationErrorMessage("This alert is already resolved and cannot be resolved again.");
        await queryClient.refetchQueries({
          queryKey: ["alert-detail", runtime.deploymentId, alertId],
        });
      } else {
        setMutationErrorMessage(error.message);
      }
    },
  });

  async function submitAction(): Promise<void> {
    if (dialogAction === null) return;

    const action = dialogAction;
    const actionReason = reason.trim();
    closeActionDialog();

    try {
      await mutation.mutateAsync({ action, reason: actionReason });
    } catch {
      // The mutation error is rendered in the alert details panel.
    }
  }

  const resetMutation = mutation.reset;
  useEffect(() => {
    setMutationErrorMessage(null);
    setDialogAction(null);
    setReason("");
    resetMutation();
  }, [alertId, resetMutation]);

  const detail = detailQuery.data;
  const resolveGrant = runtime.operationGrants.find(
    (grant) => grant.operation_id === "alert_resolve"
  );
  const canResolve =
    resolveGrant?.permissions.includes("deployment.notifications.resolve") ?? false;
  const timelineEvents: TimelineEvent[] =
    detail == null
      ? []
      : [
          ...detail.observation_history.map((observation, index) => ({
            type: "observation" as const,
            observation,
            previous: index === 0 ? null : detail.observation_history[index - 1],
          })),

          ...(detail.resolved_at == null
            ? []
            : [
                {
                  type: "resolve" as const,
                  timestamp: detail.resolved_at,
                  subject: detail.resolved_by,
                  reason: detail.resolve_reason,
                },
              ]),
        ].sort((a, b) => {
          const timestampA = a.type === "observation" ? a.observation.observed_at : a.timestamp;
          const timestampB = b.type === "observation" ? b.observation.observed_at : b.timestamp;
          return (
            new Date(timestampB).getTime() - new Date(timestampA).getTime() ||
            a.type.localeCompare(b.type)
          );
        });
  const latestObservation = detail?.observation_history.at(-1);
  const displayStatus =
    detail?.status === "active" || detail?.status === "resolved" ? detail.status : null;

  return (
    <RightRailContainer
      ariaLabel="Alert details"
      floating={floating}
      header={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {onBack != null && (
            <IconButton aria-label="Back to notifications" size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Alert details
          </Typography>
          {onClose != null && (
            <IconButton aria-label="Close alert details" size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      }
      footer={
        detail == null ? undefined : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              disabled={!canResolve || detail.status !== "active" || mutation.isPending}
              onClick={() => openActionDialog("resolve")}
              startIcon={
                mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              Resolve
            </Button>
          </Box>
        )
      }
    >
      {!hasAlertDetailGrant && (
        <Typography color="text.secondary">
          Alert details are unavailable for this session.
        </Typography>
      )}
      {detailQuery.isLoading && <CircularProgress aria-label="Loading alert details" />}
      {detailQuery.isError && <Typography color="error">{detailQuery.error.message}</Typography>}
      {detail != null && (
        <>
          <ConsoleSurface sx={{ p: 1.5, display: "grid", gap: 1.25 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
              {detail.notification.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detail.notification.message}
            </Typography>
          </ConsoleSurface>

          <ConsoleSurface sx={{ p: 1.5, display: "grid", gap: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Alert information
            </Typography>
            <Box
              component="table"
              aria-label="Alert status and severity"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                "& th, & td": { py: 0.75, textAlign: "left", verticalAlign: "middle" },
                "& th": {
                  width: "38%",
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                },
                "& tr + tr": { borderTop: 1, borderColor: "divider" },
              }}
            >
              <tbody>
                {displayStatus !== null && (
                  <tr>
                    <Box component="th" scope="row">
                      Status
                    </Box>
                    <Box component="td">
                      <StatusBadge severity={displayStatus} label={displayStatus} />
                    </Box>
                  </tr>
                )}
                <tr>
                  <Box component="th" scope="row">
                    Severity
                  </Box>
                  <Box component="td">
                    <SeverityAccent level={detail.severity} label={detail.severity} />
                  </Box>
                </tr>
                <tr>
                  <Box component="th" scope="row">
                    Scope
                  </Box>
                  <Box component="td" sx={{ fontWeight: 600 }}>
                    {getScopeLabel(detail.scope, detail.scope_display)}
                  </Box>
                </tr>
                {detail.scope.type !== "area" && detail.scope_display.area_name !== null && (
                  <tr>
                    <Box component="th" scope="row">
                      Area
                    </Box>
                    <Box component="td" sx={{ fontWeight: 600 }}>
                      {detail.scope_display.area_name}
                    </Box>
                  </tr>
                )}
                <tr>
                  <Box component="th" scope="row">
                    Raised
                  </Box>
                  <Box component="td" sx={{ fontWeight: 600 }}>
                    {formatNotificationCreatedAt(detail.raised_at)}
                  </Box>
                </tr>
                <tr>
                  <Box component="th" scope="row">
                    Updated
                  </Box>
                  <Box component="td" sx={{ fontWeight: 600 }}>
                    {formatNotificationCreatedAt(detail.updated_at)}
                  </Box>
                </tr>
              </tbody>
            </Box>
            {latestObservation !== undefined &&
              latestObservation.evidence.matched_conditions.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Latest evidence
                  </Typography>
                  {latestObservation.evidence.matched_conditions.map((condition) => (
                    <EvidenceCondition key={condition.condition_id} condition={condition} />
                  ))}
                </>
              )}
          </ConsoleSurface>

          {timelineEvents.length > 0 && (
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Lifecycle changes
              </Typography>
              <Box sx={{ display: "grid", gap: 1.25 }}>
                {timelineEvents.map((event, index) =>
                  event.type === "observation" ? (
                    <ObservationEvent
                      key={`observation-${event.observation.observed_at}-${index}`}
                      observation={event.observation}
                      previous={event.previous}
                    />
                  ) : (
                    <LifecycleActionEvent key={`${event.type}-${event.timestamp}`} event={event} />
                  )
                )}
              </Box>
            </Box>
          )}
          {mutation.isError && (
            <Typography color="error">{mutationErrorMessage ?? mutation.error.message}</Typography>
          )}
        </>
      )}
      <Dialog open={dialogAction !== null} onClose={closeActionDialog} fullWidth maxWidth="xs">
        <DialogTitle>Resolve alert</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <Typography
              component="label"
              htmlFor="alert-reason"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              Reason (optional)
            </Typography>
            <TextField
              id="alert-reason"
              autoFocus
              fullWidth
              hiddenLabel
              multiline
              minRows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              slotProps={{
                htmlInput: { maxLength: 250, "aria-describedby": "alert-reason-count" },
              }}
              helperText={<span id="alert-reason-count">{reason.length} / 250</span>}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={mutation.isPending || dialogAction === null}
            onClick={() => void submitAction()}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </RightRailContainer>
  );
}
